/**
 * Client-side billing service. Thin wrapper over the Stripe Cloud Functions.
 *
 * We use Stripe Checkout in hosted-redirect mode: the function returns a
 * `url` and we navigate to it — so no @stripe/stripe-js dependency is needed on
 * the client. The same pattern is used for the Customer Portal (manage/cancel).
 *
 * These call into functions that are inert until the Stripe secrets are set, so
 * the calling UI is only shown when `BILLING_ENABLED` is true.
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { STRIPE_PRICE_IDS, type BillingInterval } from '../config/plans';

type CheckoutResponse = { url: string };
type PortalResponse = { url: string };

/**
 * Start a subscription checkout for the given interval. On success the browser
 * is redirected to Stripe's hosted checkout page; control does not return.
 */
export async function startCheckout(interval: BillingInterval): Promise<void> {
  const priceId = STRIPE_PRICE_IDS[interval];
  return startCheckoutForPrice(priceId, interval);
}

/**
 * Start checkout for an explicit Price ID (used for team/B2B plans, which have
 * many prices). Redirects to Stripe-hosted checkout on success.
 */
export async function startCheckoutForPrice(
  priceId: string,
  interval: BillingInterval,
): Promise<void> {
  if (!priceId) {
    throw new Error('Billing is not configured yet. Please try again later.');
  }

  const createCheckout = httpsCallable<
    { priceId: string; interval: BillingInterval; returnUrl: string },
    CheckoutResponse
  >(functions, 'createCheckoutSession');

  const returnUrl = `${window.location.origin}${window.location.pathname}#app`;
  const { data } = await createCheckout({ priceId, interval, returnUrl });
  if (!data?.url) throw new Error('Could not start checkout. Please try again.');
  window.location.assign(data.url);
}

/**
 * Open the Stripe Customer Portal so the user can update payment method,
 * switch plans, or cancel. Redirects the browser to the portal.
 */
export async function openBillingPortal(): Promise<void> {
  const createPortal = httpsCallable<{ returnUrl: string }, PortalResponse>(
    functions,
    'createPortalSession',
  );
  const returnUrl = `${window.location.origin}${window.location.pathname}#app`;
  const { data } = await createPortal({ returnUrl });
  if (!data?.url) throw new Error('Could not open the billing portal. Please try again.');
  window.location.assign(data.url);
}

/**
 * Reads and clears the `?checkout=success|cancel` query param Stripe appends on
 * return. Returns the outcome (or null) so the app can show a toast. Clearing it
 * keeps the URL clean and prevents the toast from re-firing on refresh.
 */
export function consumeCheckoutResult(): 'success' | 'cancel' | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const result = params.get('checkout');
  if (result !== 'success' && result !== 'cancel') return null;
  params.delete('checkout');
  const query = params.toString();
  const newUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
  window.history.replaceState({}, '', newUrl);
  return result;
}
